import React, {
  createContext,
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import FeatureToggleProvider from './FeatureToggleProvider';
import { ProfileInformations } from '../templates/profile/interface';
import { getSSOUrls } from '../common/utils/ssoEndPoints';
import { getDecryptedValue, setEncryptedValue } from '../common/utils/LocalStorageUtils';
import { listOfFeatures } from './FeatureLists';

const USER_TELECOM_CACHE_KEY = 'userTelecom';
const PROXY_AUTH_LINK_CACHE_KEY = 'proxyAuthLinkData';
const MEMBER_COVERAGE_END_DATE_CACHE_KEY = 'memberCoverageEndDate';
const EMPTY_PROXY_AUTH_LINK_DATA = {
  correlationId: '',
  forgerockUuid: '',
  token: '',
};

const getInitialProxyAuthorizationLinkData = () => {
  try {
    const stored = sessionStorage.getItem(PROXY_AUTH_LINK_CACHE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        ...EMPTY_PROXY_AUTH_LINK_DATA,
        correlationId: parsed?.correlationId ?? '',
        forgerockUuid: parsed?.forgerockUuid ?? '',
      };
    }
  } catch (error) {
    console.error('Failed to load proxy auth link cache:', error);
  }

  return { ...EMPTY_PROXY_AUTH_LINK_DATA };
};

export const GlobalContext = createContext({} as any);
const GlobalProvider = ({ children }: any) => {
  const [userEncodedData, setUserEncodedData] = useState([]);
  const [getFeature, setFeature] = useState(listOfFeatures);
  const [zipValue, setZipValue] = useState('');
  const [addressValue, setAddressValue] = useState('');
  const [proxyDataValue, setProxyDataValue] = useState('');
  const [proxyData, setProxyData] = useState([]);
  const [age, setAge] = useState('');
  const [enableView, setEnableView] = useState(false);
  const [proxySelected, setProxySelected] = useState([]);
  const [proxyMemberId, setProxyMemberId] = useState('');
  const [proxyDropDown, setProxyDropDown] = useState(false);
  const [enableDelete, setEnableDelete] = useState(false);
  const [updateProxy, setUpdateProxy] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [loginMID, setLoginMID] = useState('');
  const [loginProxyId, setLoginProxyId] = useState('');
  const [loginMemberId, setLoginMemberId] = useState('');
  const [getAuthToken, setAuthToken] = useState('');
  const [proxyUniqueId, setProxyUniqueId] = useState('');
  const [profileState, setProfileState] = useState<ProfileInformations>({});
  const [xauthToken, setXauthToken] = useState('');
  const [xsentaraTokenID, setXsentaraTokenID] = useState('');
  const [isDesignatedRepresentative, setIsDesignatedRepresentative] =
    useState(false);
  const [designatedDropdownLink, setDesignatedDropdownLink] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [confirmProxy, setConfirmProxy] = useState([]);
  const [memberProxyData, setMemberProxyData] = useState([]);
  const [proxyUpdate, setProxyUpdate] = useState(false);
  const [isGuestSwitch, setIsGuestSwitch] = useState(false);
  const [isBacktoDashBoard, setIsBacktoDashBoard] = useState(false);
  const [primaryUser, setPrimaryUser] = useState('');
  const [selectedIdCardMemberId, setSelectedIdCardMemberId] =
    useState<string>('');
  const [planName, setPlanName] = useState<string>('');
  const [planWellness, setPlanWellness] = useState<string>('');
  const [serviceNumber, setServiceNumber] = useState<string>('');
  const [isMemberTabDefault, setIsMemberTabDefault] = useState<boolean>(false);
  const [ssoLink, setSSOLink] = useState<string>('');
  const [isDesignatedTabShow, setIsDesignatedTabShow] =
    useState<boolean>(false);
  const [onEmailUpdate, setOnEmailUpdate] = useState<boolean>(false);
  const [ssoEndPoints, setSsoEndPoints] = useState<any>(null);
  const [logging, setLogging] = useState<any>(null);
  const [monitoring, setMonitoring] = useState<any>(null);
  const [healthEquityHSA, setHealthEquityHSA] = useState<boolean>(false);
  const [accountType, setAccountType] = useState('');
  const [userTelecom, setUserTelecom] = useState<any[]>([]);
  const [drRegistrationFlow, setDrRegistrationFlow] = useState<{
    isActive: boolean;
    type: 'regular' | 'childOnly' | null;
  }>({ isActive: false, type: null });
  const [proxyAuthorizationLinkData, setProxyAuthorizationLinkData] = useState<{
    correlationId: string;
    forgerockUuid: string;
    token: string;
  }>(() => getInitialProxyAuthorizationLinkData());
  const [selectedProxyAccount, setSelectedProxyAccount] = useState<string>('current');
  const [approvedDesignators, setApprovedDesignators] = useState<
    Array<{ name: string; status: string }>
  >([]);
  const isDRProfileView = approvedDesignators.length > 0;
  // Bumped after a guest proxy action so the side menu re-reads the shared-access state without a reload.
  const [sharedAccessRefreshKey, setSharedAccessRefreshKey] = useState(0);
  // undefined = not fetched, null = no coverage end date (204/missing), string = ISO end date
  const [memberCoverageEndDate, setMemberCoverageEndDate] = useState<
    string | null | undefined
  >(() => {
    // Rehydrate so the DR form keeps the date across a reload without re-fetching.
    try {
      const stored = sessionStorage.getItem(MEMBER_COVERAGE_END_DATE_CACHE_KEY);
      return stored === null ? undefined : JSON.parse(stored);
    } catch (error) {
      console.error('Failed to load coverage end date cache:', error);
      return undefined;
    }
  });


  const cachedTelecom = useRef(false);

  useEffect(() => {
    const loadUserTelecom = async () => {
      try {
        const cached = await getDecryptedValue(USER_TELECOM_CACHE_KEY);

        if (cached) {
          const telecom =
            typeof cached === 'string' ? JSON.parse(cached) : cached;

          if (Array.isArray(telecom)) {
            setUserTelecom(telecom);
          }
        }
      } catch (error) {
        console.error('Failed to load telecom cache:', error);
      } finally {
        cachedTelecom.current = true;
      }
    };

    void loadUserTelecom();
  }, []);

  useEffect(() => {
    if (!cachedTelecom.current) return;

    void setEncryptedValue(
      USER_TELECOM_CACHE_KEY,
      JSON.stringify(userTelecom)
    ).catch((error) => {
      console.error('Failed to persist telecom cache:', error);
    });
  }, [userTelecom]);

  // Persist proxy link identifiers so the status popup can re-fetch after a reload.
  useEffect(() => {
    try {
      const { correlationId, forgerockUuid } = proxyAuthorizationLinkData;
      if (correlationId || forgerockUuid) {
        sessionStorage.setItem(
          PROXY_AUTH_LINK_CACHE_KEY,
          JSON.stringify({ correlationId, forgerockUuid })
        );
      } else {
        sessionStorage.removeItem(PROXY_AUTH_LINK_CACHE_KEY);
      }
    } catch (error) {
      console.error('Failed to persist proxy auth link cache:', error);
    }
  }, [proxyAuthorizationLinkData]);

  // Persist the coverage end date so the DR form retains it after a reload.
  useEffect(() => {
    try {
      if (memberCoverageEndDate === undefined) {
        sessionStorage.removeItem(MEMBER_COVERAGE_END_DATE_CACHE_KEY);
      } else {
        sessionStorage.setItem(
          MEMBER_COVERAGE_END_DATE_CACHE_KEY,
          JSON.stringify(memberCoverageEndDate)
        );
      }
    } catch (error) {
      console.error('Failed to persist coverage end date cache:', error);
    }
  }, [memberCoverageEndDate]);

  // Function to fetch SSO URLs
  const refetchSSOUrls = useCallback(async () => {
    try {
      const urls = await getSSOUrls();
      setSsoEndPoints(urls);
    } catch (error) {
      console.error('Error fetching SSO URLs:', error);
    }
  }, []);

  // Fetch SSO URLs on mount
  useEffect(() => {
    refetchSSOUrls();
  }, [refetchSSOUrls]);

  // Listen for i18next's built-in languageChanged event
  useEffect(() => {
    let cleanup: (() => void) | undefined;

    // Dynamically import i18n to avoid circular dependency
    import('../i18n')
      .then((i18nModule) => {
        const i18n = i18nModule.default;

        const handleLanguageChange = () => {
          refetchSSOUrls();
        };

        i18n.on('languageChanged', handleLanguageChange);

        cleanup = () => {
          i18n.off('languageChanged', handleLanguageChange);
        };
      })
      .catch((error) => {
        console.error('Failed to load i18n module:', error);
      });

    return () => {
      cleanup?.();
    };
  }, [refetchSSOUrls]);

  //This Function will be called on signout to clear all context
  const clearContextValues = () => {
    setUserEncodedData([]);
    setFeature(listOfFeatures);
    setZipValue('');
    setAddressValue('');
    setProxyDataValue('');
    setProxyData([]);
    setAge('');
    setEnableView(false);
    setProxySelected([]);
    setProxyDropDown(false);
    setEnableDelete(false);
    setUpdateProxy(false);
    setShowContent(false);
    setLoginMID('');
    setLoginProxyId('');
    setLoginMemberId('');
    setIsDesignatedRepresentative(false);
    setDesignatedDropdownLink(false);
    setViewModal(false);
    setConfirmProxy([]);
    setMemberProxyData([]);
    setProxyUpdate(false);
    setIsGuestSwitch(false);
    setIsBacktoDashBoard(false);
    setPrimaryUser('');
    setSelectedIdCardMemberId('');
    setPlanName('');
    setPlanWellness('');
    setServiceNumber('');
    setIsMemberTabDefault(false);
    setSSOLink('');
    setIsDesignatedTabShow(false);
    setProxyUniqueId('');
    setOnEmailUpdate(false);
    setProfileState({});
    setLogging(null);
    setMonitoring(null);
    setHealthEquityHSA(false);
    setAccountType('');
    setUserTelecom([]);
    setDrRegistrationFlow({ isActive: false, type: null });
    setProxyAuthorizationLinkData({ ...EMPTY_PROXY_AUTH_LINK_DATA });
    setSelectedProxyAccount('current');
    setApprovedDesignators([]);
    setSharedAccessRefreshKey(0);
    setMemberCoverageEndDate(undefined);
  };

  const globalContextVal = useMemo(
    () => ({
      userEncodedData,
      setUserEncodedData,
      getFeature,
      setFeature,
      setZipValue,
      zipValue,
      setAddressValue,
      addressValue,
      setProxyDataValue,
      proxyDataValue,
      loginMemberId,
      setLoginMemberId,
      setProxyData,
      proxyData,
      age,
      setAge,
      proxySelected,
      setProxySelected,
      setEnableView,
      enableView,
      proxyDropDown,
      setProxyDropDown,
      enableDelete,
      setEnableDelete,
      updateProxy,
      setUpdateProxy,
      setShowContent,
      showContent,
      isDesignatedRepresentative,
      setIsDesignatedRepresentative,
      designatedDropdownLink,
      setDesignatedDropdownLink,
      memberProxyData,
      setMemberProxyData,
      viewModal,
      setViewModal,
      setProxyUpdate,
      proxyUpdate,
      setConfirmProxy,
      confirmProxy,
      isGuestSwitch,
      setIsGuestSwitch,
      isBacktoDashBoard,
      setIsBacktoDashBoard,
      primaryUser,
      setPrimaryUser,
      selectedIdCardMemberId,
      setSelectedIdCardMemberId,
      planName,
      setPlanName,
      planWellness,
      setPlanWellness,
      loginMID,
      setLoginMID,
      loginProxyId,
      setLoginProxyId,
      serviceNumber,
      setServiceNumber,
      setAuthToken,
      getAuthToken,
      setProxyMemberId,
      proxyMemberId,
      isMemberTabDefault,
      setIsMemberTabDefault,
      setSSOLink,
      ssoLink,
      isDesignatedTabShow,
      setIsDesignatedTabShow,
      clearContextValues,
      setProxyUniqueId,
      setProfileState,
      profileState,
      proxyUniqueId,
      xauthToken,
      setXauthToken,
      xsentaraTokenID,
      setXsentaraTokenID,
      onEmailUpdate,
      setOnEmailUpdate,
      ssoEndPoints,
      refetchSSOUrls,
      logging,
      setLogging,
      monitoring,
      setMonitoring,
      healthEquityHSA,
      setHealthEquityHSA,
      accountType,
      setAccountType,
      userTelecom,
      setUserTelecom,
      drRegistrationFlow,
      setDrRegistrationFlow,
      proxyAuthorizationLinkData,
      setProxyAuthorizationLinkData,
      selectedProxyAccount,
      setSelectedProxyAccount,
      isDRProfileView,
      approvedDesignators,
      setApprovedDesignators,
      sharedAccessRefreshKey,
      setSharedAccessRefreshKey,
      memberCoverageEndDate,
      setMemberCoverageEndDate,
    }),
    [
      addressValue,
      age,
      confirmProxy,
      designatedDropdownLink,
      enableDelete,
      enableView,
      getFeature,
      isDesignatedRepresentative,
      loginMemberId,
      memberProxyData,
      proxyData,
      proxyDataValue,
      proxyDropDown,
      proxySelected,
      proxyUpdate,
      showContent,
      updateProxy,
      userEncodedData,
      viewModal,
      zipValue,
      isGuestSwitch,
      setIsBacktoDashBoard,
      isBacktoDashBoard,
      primaryUser,
      setPrimaryUser,
      selectedIdCardMemberId,
      setSelectedIdCardMemberId,
      planName,
      setPlanName,
      planWellness,
      setPlanWellness,
      loginMID,
      setLoginMID,
      loginProxyId,
      setLoginProxyId,
      serviceNumber,
      setServiceNumber,
      setAuthToken,
      getAuthToken,
      setProxyMemberId,
      proxyMemberId,
      isMemberTabDefault,
      setIsMemberTabDefault,
      setSSOLink,
      ssoLink,
      isDesignatedTabShow,
      setIsDesignatedTabShow,
      clearContextValues,
      setProxyUniqueId,
      proxyUniqueId,
      setProfileState,
      profileState,
      xauthToken,
      setXauthToken,
      xsentaraTokenID,
      setXsentaraTokenID,
      onEmailUpdate,
      setOnEmailUpdate,
      ssoEndPoints,
      logging,
      setLogging,
      monitoring,
      setMonitoring,
      healthEquityHSA,
      setHealthEquityHSA,
      accountType,
      setAccountType,
      userTelecom,
      drRegistrationFlow,
      proxyAuthorizationLinkData,
      selectedProxyAccount,
      setSelectedProxyAccount,
      isDRProfileView,
      approvedDesignators,
      setApprovedDesignators,
      sharedAccessRefreshKey,
      memberCoverageEndDate,
    ]
  );
  return (
    <GlobalContext.Provider value={globalContextVal}>
      {/* Wrap children with FeatureToggleProvider context provider */}
      <FeatureToggleProvider>{children}</FeatureToggleProvider>
    </GlobalContext.Provider>
  );
};

export default GlobalProvider;
